

import { useEffect, useState } from "react";
import { getAuth, getIdTokenResult, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirebaseServices } from "@/lib/firebase/client";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { Skeleton } from "@/components/ui/Skeleton";

interface PayoutDoc {
  id: string;
  amount: number;
  paypalEmail?: string;
  status?: string;
  createdAt?: any;
  processedAt?: any;
  transactionId?: string;
}

export default function PayoutHistory() {
  const [payouts, setPayouts] = useState<PayoutDoc[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) return;

    let unsubSnapshot: (() => void) | null = null;

    const handleUser = async (user: any) => {
      if (!user) {
        setPayouts([]);
        setLoading(false);
        return;
      }

      // Determine admin claim from ID token
      try {
        const idr = await getIdTokenResult(user);
        setIsAdmin(Boolean(idr?.claims?.admin));
      } catch (e) {
        setIsAdmin(false);
      }

      const payoutsRef = collection(services.db, `users/${user.uid}/payouts`);
      const q = query(payoutsRef, orderBy('createdAt', 'desc'));

      unsubSnapshot = onSnapshot(q, (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as PayoutDoc));
        setPayouts(docs);
        setLoading(false);
      }, (err) => {
        console.error('Failed to load payouts', err);
        setLoading(false);
      });
    };

    const unsubscribeAuth = onAuthStateChanged(services.auth, (u) => {
      handleUser(u);
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubSnapshot) unsubSnapshot();
    };
  }, []);

  const updateStatus = async (payoutId: string, status: string) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const functions = getFunctions();
    const fn = httpsCallable(functions, 'updatePayoutStatus');
    try {
      const uid = user.uid;
      await fn({ uid, payoutId, status });
      // function will update Firestore; onSnapshot will reflect changes
    } catch (e) {
      console.error('updateStatus failed', e);
      const msg = (e as any)?.message || String(e);
      alert('Failed to update payout status: ' + msg);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <h3 className="mb-3 text-lg font-semibold text-white">Payout history</h3>
        <div className="space-y-3">
          <div className="rounded-xl border border-white/5 bg-black/20 p-3 space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
          <div className="rounded-xl border border-white/5 bg-black/20 p-3 space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
      </div>
    );
  }

  if (!payouts.length) return <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">No payouts yet.</div>;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white">
      <h3 className="mb-3 text-lg font-semibold">Payout history</h3>
      <div className="space-y-3">
        {payouts.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-black/20 p-3">
            <div>
              <div className="text-sm font-medium">${(p.amount || 0).toFixed(2)}</div>
              <div className="text-xs text-white/60">{p.paypalEmail || 'PayPal'}</div>
              <div className="text-xs text-white/50">{p.createdAt && p.createdAt.toDate ? p.createdAt.toDate().toLocaleString() : String(p.createdAt)}</div>
              {p.processedAt && <div className="text-xs text-white/50">Processed: {p.processedAt.toDate ? p.processedAt.toDate().toLocaleString() : String(p.processedAt)}</div>}
            </div>
            <div className="text-right">
              <div className="text-sm font-medium">{p.status || 'pending'}</div>
              {isAdmin && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => updateStatus(p.id, 'paid')} className="rounded px-3 py-1 bg-emerald-400 text-black text-xs">Mark Paid</button>
                  <button onClick={() => updateStatus(p.id, 'failed')} className="rounded px-3 py-1 bg-rose-500 text-white text-xs">Mark Failed</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
